import {
  getVariableValue,
  resolveTokens,
  stringifyVariableValue,
  TOKEN_NAME_PATTERN,
} from "./resolve-tokens";

// Strict double-brace only: not the outer pair of a `{{{var}}}` (the `(?!\{)`
// after the opening braces, the `(?!\})` after the closing ones), which is
// left for the html-block parser to consume. The name part is the shared
// TOKEN_NAME_PATTERN, so a date pill keeps its `|D MMM YYYY` format tail
// (spaces included) instead of falling back to text.
//
// No prefix group: an earlier `(^|[^{])` guard CONSUMED the character before
// `{{`, so with two adjacent tokens (`{{a}}{{b}}`) the second had nothing left
// to consume and never became a pill — it rendered as raw text whenever the
// element was not being edited. Lookbehind is not an option (Safari < 16.4).
export const VARIABLE_TOKEN_PATTERN = `\\{\\{(?!\\{)\\s*(${TOKEN_NAME_PATTERN})\\s*\\}\\}(?!\\})`;

const TOKEN_RE = new RegExp(VARIABLE_TOKEN_PATTERN, "g");

export const VARIABLE_PILL_CLASS = "sd-variable-pill";
export const VARIABLE_PILL_ATTR = "data-variable-name";

/**
 * Text shown inside a pill chip. When the variable resolves to a value
 * (version mode) the chip shows that value; otherwise (authoring, or an unset
 * version value) it shows the raw `{{name}}` token so the brackets stay
 * visible. Storage is unaffected — the save path serializes by
 * `data-variable-name`, not by this label.
 */
export const pillDisplayText = (name: string, resolved: string): string =>
  resolved.length > 0 ? resolved : `{{${name}}}`;

/** Extra Σ badge class for a calculation-variable pill (green accent — see
 * Richtext.scss's `.sd-variable-pill--calc`). */
export const VARIABLE_PILL_CALC_CLASS = "sd-variable-pill--calc";

/** Extra badge class for a repeatable-field pill (blue accent — see
 * Richtext.scss's `.sd-variable-pill--repeatable`). */
export const VARIABLE_PILL_REPEATABLE_CLASS = "sd-variable-pill--repeatable";

/**
 * Optional per-pill override, supplied by callers that know a token belongs
 * to a variant variable kind (calculation, repeatable field, …). Kept as a
 * generic decorator — rather than importing feature modules here — so this
 * shared/utils module stays feature-agnostic; every existing call site that
 * omits it keeps its exact prior behavior. `className` lets a decorator pick
 * its own badge class instead of the calc default (see `applyPill`).
 */
export type PillDecorator = (
  name: string,
) => { text: string; title?: string; className?: string } | undefined;

/** First decorator that returns a result wins; `undefined` entries are skipped. */
export const composePillDecorators =
  (...decorators: Array<PillDecorator | undefined>): PillDecorator =>
  (name) => {
    for (const decorate of decorators) {
      const deco = decorate?.(name);
      if (deco) return deco;
    }
    return undefined;
  };

const PILL_VARIANT_CLASSES = [
  VARIABLE_PILL_CALC_CLASS,
  VARIABLE_PILL_REPEATABLE_CLASS,
];

const applyPill = (
  span: HTMLSpanElement,
  name: string,
  fallbackText: string,
  decorate?: PillDecorator,
): void => {
  const deco = decorate?.(name);
  for (const cls of PILL_VARIANT_CLASSES) span.classList.remove(cls);
  if (deco) {
    // A decorator names its own variant class (calc → green, repeatable →
    // badge); one without a class keeps the plain pill look (e.g. dates).
    if (deco.className) span.classList.add(deco.className);
    span.textContent = deco.text;
    if (deco.title) span.title = deco.title;
    else span.removeAttribute("title");
  } else {
    span.textContent = fallbackText;
    span.removeAttribute("title");
  }
};

export const createPillElement = (
  doc: Document,
  name: string,
  resolved: string,
  decorate?: PillDecorator,
): HTMLSpanElement => {
  const span = doc.createElement("span");
  span.className = VARIABLE_PILL_CLASS;
  span.setAttribute(VARIABLE_PILL_ATTR, name);
  // Non-editable atom (PandaDoc model): the browser itself never places a
  // caret inside, never selects part of it, and Backspace/Delete remove it
  // whole — no timing games, no flicker. Froala's inline formatting would
  // skip a non-editable node; the editor config sets
  // `allowStylingOnNonEditable: true` so bold/italic/colour wrap the pill
  // together with the surrounding run (see froala/config.ts).
  span.setAttribute("contenteditable", "false");
  applyPill(span, name, pillDisplayText(name, resolved), decorate);
  return span;
};

const isInsideElement = (node: Node, selector: string): boolean => {
  let current: Node | null = node.parentNode;
  while (current && current.nodeType === Node.ELEMENT_NODE) {
    if ((current as Element).matches(selector)) return true;
    current = current.parentNode;
  }
  return false;
};

const replaceTokensInTextNode = (
  textNode: Text,
  data: Record<string, unknown> | undefined | null,
  decorate?: PillDecorator,
): boolean => {
  const text = textNode.textContent ?? "";
  if (text.indexOf("{{") === -1) return false;

  const doc = textNode.ownerDocument;
  if (!doc) return false;

  const fragment = doc.createDocumentFragment();
  let lastIndex = 0;
  let didReplace = false;
  TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null = TOKEN_RE.exec(text);
  while (match !== null) {
    didReplace = true;
    const tokenIndex = match.index;
    if (tokenIndex > lastIndex) {
      fragment.appendChild(
        doc.createTextNode(text.slice(lastIndex, tokenIndex)),
      );
    }
    const name = match[1];
    const raw = getVariableValue(data, name);
    const resolved = stringifyVariableValue(raw);
    fragment.appendChild(createPillElement(doc, name, resolved, decorate));
    lastIndex = match.index + match[0].length;
    match = TOKEN_RE.exec(text);
  }

  if (!didReplace) return false;

  if (lastIndex < text.length) {
    fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
  }

  textNode.parentNode?.replaceChild(fragment, textNode);
  return true;
};

export const refreshPillValues = (
  root: Element,
  data: Record<string, unknown> | undefined | null,
  decorate?: PillDecorator,
) => {
  const pills = root.querySelectorAll(`.${VARIABLE_PILL_CLASS}`);
  for (const pill of Array.from(pills)) {
    const name = pill.getAttribute(VARIABLE_PILL_ATTR);
    if (!name) continue;
    const raw = getVariableValue(data, name);
    const resolved = stringifyVariableValue(raw);
    applyPill(
      pill as HTMLSpanElement,
      name,
      pillDisplayText(name, resolved),
      decorate,
    );
  }
};

export const templateToPillsInDom = (
  root: Element,
  data: Record<string, unknown> | undefined | null,
  decorate?: PillDecorator,
) => {
  const doc = root.ownerDocument ?? document;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    const text = node.textContent ?? "";
    if (
      text.indexOf("{{") !== -1 &&
      !isInsideElement(node, `.${VARIABLE_PILL_CLASS}`)
    ) {
      targets.push(node as Text);
    }
    node = walker.nextNode();
  }
  for (const textNode of targets) {
    replaceTokensInTextNode(textNode, data, decorate);
  }
};

export const templateToPillsHtml = (
  html: string,
  data: Record<string, unknown> | undefined | null,
  decorate?: PillDecorator,
): string => {
  if (!html || html.indexOf("{{") === -1) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  templateToPillsInDom(doc.body, data, decorate);
  return doc.body.innerHTML;
};

// Inline formatting tags the editor may apply inside a pill (bold, italic,
// colour spans, …). When present, they must survive the pill → `{{token}}`
// serialization, otherwise styling a variable is lost on save.
const PILL_FORMAT_TAGS = new Set([
  "STRONG",
  "B",
  "EM",
  "I",
  "U",
  "S",
  "STRIKE",
  "SUB",
  "SUP",
  "MARK",
  "SPAN",
  "FONT",
]);

/**
 * Builds the replacement node for a pill: the `{{name}}` token re-wrapped in the
 * same inline-formatting chain the user applied inside the pill (e.g. a nested
 * `<strong>`), plus any style set on the pill span itself. Falls back to a bare
 * token text node when the pill carries no formatting.
 */
const buildPillTokenNode = (
  pill: Element,
  name: string,
  doc: Document,
): Node => {
  let node: Node = doc.createTextNode(`{{${name}}}`);

  // Lift a chain of single formatting-element wrappers (e.g. <strong><em>…).
  const wrappers: Element[] = [];
  let container: Element = pill;
  while (
    container.childNodes.length === 1 &&
    container.firstElementChild &&
    PILL_FORMAT_TAGS.has(container.firstElementChild.tagName)
  ) {
    const el = container.firstElementChild;
    const clone = doc.createElement(el.tagName.toLowerCase());
    const style = el.getAttribute("style");
    if (style) clone.setAttribute("style", style);
    wrappers.push(clone);
    container = el;
  }
  for (let i = wrappers.length - 1; i >= 0; i--) {
    wrappers[i].appendChild(node);
    node = wrappers[i];
  }

  // Preserve a style applied directly to the pill span (e.g. colour).
  const pillStyle = pill.getAttribute("style");
  if (pillStyle) {
    const styleSpan = doc.createElement("span");
    styleSpan.setAttribute("style", pillStyle);
    styleSpan.appendChild(node);
    node = styleSpan;
  }

  return node;
};

/**
 * Move pills nested inside `pill` out to just after it, order preserved.
 * Nesting only happens when an insert landed inside an existing atom (see
 * insert-handler.ts), and `buildPillTokenNode` replaces the whole outer pill
 * with a single `{{outer}}` text node — which would take the inner pill, and
 * the variable it holds, with it. Hoisting first makes the pair serialize as
 * `{{outer}}{{inner}}` instead. Only the outermost nested pills are moved:
 * appending one relocates its whole subtree, so anything nested deeper is no
 * longer under `pill` and is handled when the caller's loop reaches the pill
 * it now sits in.
 */
const hoistNestedPills = (pill: Element): void => {
  const nested = pill.querySelectorAll(`.${VARIABLE_PILL_CLASS}`);
  if (nested.length === 0) return;
  const doc = pill.ownerDocument;
  if (!doc) return;
  const fragment = doc.createDocumentFragment();
  for (const inner of Array.from(nested)) {
    if (pill.contains(inner)) fragment.appendChild(inner);
  }
  pill.parentNode?.insertBefore(fragment, pill.nextSibling);
};

export const pillsToTemplateInDom = (root: Element) => {
  const pills = root.querySelectorAll(`.${VARIABLE_PILL_CLASS}`);
  for (const pill of Array.from(pills)) {
    // A pill always carries text (the value, the token, a decorator label);
    // one with no content at all is the hollow span Froala's range deletion
    // leaves behind (see pill-atomic.ts removeDeletedPills). Serialising it
    // back to `{{name}}` would resurrect a variable the user deleted.
    if (pill.childNodes.length === 0) {
      pill.remove();
      continue;
    }
    const name = pill.getAttribute(VARIABLE_PILL_ATTR);
    if (!name) continue;
    const doc = pill.ownerDocument;
    if (!doc) continue;
    // Hoisted pills stay in this loop's snapshot (querySelectorAll is in
    // document order, outer before inner), so each one is serialized in turn
    // at its new position after the outer token.
    hoistNestedPills(pill);
    pill.parentNode?.replaceChild(buildPillTokenNode(pill, name, doc), pill);
  }
};

export const pillsToTemplateHtml = (html: string): string => {
  if (!html || html.indexOf(VARIABLE_PILL_CLASS) === -1) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  pillsToTemplateInDom(doc.body);
  return doc.body.innerHTML;
};

/**
 * Read the template text from a contenteditable region that may contain
 * variable pills. Pills are emitted as `{{name}}`; surrounding text nodes
 * are concatenated as-is.
 */
export const pillsToTemplateText = (root: Element): string => {
  const clone = root.cloneNode(true) as Element;
  pillsToTemplateInDom(clone);
  return clone.textContent ?? "";
};

// Same shape as TOKEN_RE, for scanning a container's full (cross-node)
// textContent.
const REPAIR_TOKEN_RE = new RegExp(VARIABLE_TOKEN_PATTERN, "g");

// Heal ONE variable token that inline formatting has split across element
// boundaries (e.g. bolding part of it yields `{{first<strong>Name}}</strong>`).
// The concatenated textContent still reads `{{firstName}}`, but the render-time
// parser matches per text node and can no longer see it, so the variable
// "breaks". Collapse the split token's DOM range back to a single plain-text
// `{{name}}`. Returns true when it repaired a token (caller re-scans).
const repairFirstSplitToken = (root: Element): boolean => {
  const doc = root.ownerDocument ?? document;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  const starts: number[] = [];
  let full = "";
  let node = walker.nextNode();
  while (node) {
    starts.push(full.length);
    nodes.push(node as Text);
    full += node.textContent ?? "";
    node = walker.nextNode();
  }
  if (full.indexOf("{{") === -1) return false;

  // Map an absolute offset to a (text node, local offset). For an END offset,
  // probe the last character (offset - 1) so a boundary offset resolves to the
  // END of the previous node rather than the START of the next — otherwise an
  // intact token that exactly fills a node looks "split".
  const locate = (
    offset: number,
    isEnd: boolean,
  ): { node: Text; offset: number } => {
    const probe = isEnd ? offset - 1 : offset;
    let lo = 0;
    let hi = nodes.length - 1;
    let idx = 0;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (starts[mid] <= probe) {
        idx = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return { node: nodes[idx], offset: offset - starts[idx] };
  };

  REPAIR_TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null = REPAIR_TOKEN_RE.exec(full);
  while (match !== null) {
    const start = match.index;
    const end = match.index + match[0].length;
    const from = locate(start, false);
    const to = locate(end, true);
    // Split only when the token spans more than one text node; a token already
    // contained in a single text node renders fine and needs no repair.
    if (from.node !== to.node) {
      const range = doc.createRange();
      range.setStart(from.node, from.offset);
      range.setEnd(to.node, to.offset);
      range.deleteContents();
      range.insertNode(doc.createTextNode(`{{${match[1]}}}`));
      return true;
    }
    match = REPAIR_TOKEN_RE.exec(full);
  }
  return false;
};

/**
 * Repair every variable token that inline formatting split across element
 * boundaries. Re-scans after each repair so DOM offsets stay valid. Safe to
 * run on any editor content; a no-op when no tokens are split.
 */
export const repairSplitVariableTokens = (root: Element): void => {
  let guard = 0;
  // Bound the loop well above any realistic token count as a safety valve.
  while (guard++ < 1000 && repairFirstSplitToken(root)) {
    // repairFirstSplitToken mutates the DOM; loop until none remain.
  }
};

export const resolveTokensInHtml = (
  html: string,
  data: Record<string, unknown> | undefined | null,
): string => {
  if (!html || html.indexOf("{{") === -1) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    if ((node.textContent ?? "").indexOf("{{") !== -1) {
      targets.push(node as Text);
    }
    node = walker.nextNode();
  }
  for (const textNode of targets) {
    textNode.textContent = resolveTokens(textNode.textContent ?? "", data);
  }
  return doc.body.innerHTML;
};
